// Scanner — discovery view. Merged "Search & preferences" panel drives both
// (1) the scan-history table's display filters AND (2) the next scan run.
// Source chips are multi-select. Run scan uses whatever sources are checked.
//
// Runs are persistent background jobs (survive page navigation + refresh).
// Two-stage orchestration: API portals → external sources, using useJobRunner.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, X, Plus } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ScanHistoryTable, { type ScanHistoryFilters } from '../components/domain/ScanHistoryTable';
import ScanActionBar from '../components/domain/ScanActionBar';
import ScanStatusBar from '../components/domain/ScanStatusBar';
import TitleFilterIndicator from '../components/domain/TitleFilterIndicator';
import Chip from '../components/ui/Chip';
import Drawer from '../components/ui/Drawer';
import Progress from '../components/ui/Progress';
import { apiGet, apiPost } from '../lib/api';
import { useJobRunner } from '../lib/useJobRunner';
import type { JobPreferences, Portal, UserProfile, TitleFilterState } from '../lib/types';

// Source chip keys — "api" is a pseudo-source that expands server-side to
// all ATS API backends (greenhouse/ashby/lever/workable).
// Sources flagged `disabled` stay in the list so the SPA UI matches the
// historical scan-history data (the colour map in ScanHistoryTable still
// renders historical glassdoor/zip rows correctly), but the chip is
// greyed out and not toggleable. Reason: in 5690 historical entries
// neither glassdoor nor zip_recruiter produced ANY results — glassdoor
// hits 400/403 anti-scraping on jobspy, zip_recruiter is US-only and
// the UK ML/AI/security-research niche has zero coverage. Keeping
// them disabled (vs deleted) preserves the choice if jobspy ever fixes
// glassdoor and is a visible explanation rather than a silent removal.
const SOURCE_CHOICES: {
  key: string;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}[] = [
  { key: 'api',           label: 'API portals' },
  { key: 'linkedin',      label: 'LinkedIn' },
  { key: 'indeed',        label: 'Indeed' },
  { key: 'glassdoor',     label: 'Glassdoor',
    disabled: true, disabledReason: 'Anti-scraping block (0 results in 5690 entries)' },
  { key: 'zip_recruiter', label: 'ZipRecruiter',
    disabled: true, disabledReason: 'US-only; no UK/EU ML/AI coverage' },
];

const DISABLED_SOURCES = new Set(
  SOURCE_CHOICES.filter((c) => c.disabled).map((c) => c.key),
);

const EXT_SOURCES = new Set(['linkedin', 'indeed', 'glassdoor', 'zip_recruiter']);

const DEFAULT_PREFS: JobPreferences = {
  search_term: '',
  location: '',
  is_remote: false,
  hours_old: null,
  job_type: '',
  job_types: [],
  country_indeed: '',
  limit_per_source: 100,
  min_comp: 0,
  only_with_salary: false,
  sources: ['api'],
};

/** Read-side migration for profiles persisted before multi-select shipped.
 *  Promotes a single legacy `job_type` value into `job_types[]`. Returns
 *  the prefs unchanged once `job_types` is already populated. */
function migrateJobTypes(p: JobPreferences): JobPreferences {
  if (Array.isArray(p.job_types) && p.job_types.length > 0) return p;
  if (p.job_type) {
    return { ...p, job_types: [p.job_type as 'fulltime' | 'contract' | 'parttime' | 'internship'] };
  }
  return { ...p, job_types: [] };
}

// Small orchestration blob — remembers which external sources the user wanted
// this run, so when API finishes the runner can start the external stage.
const ORCH_KEY = 'nice2job.scanner.orchestration';
interface Orchestration { wantExt: boolean; extSources: string[]; payload: string; }

function loadOrch(): Orchestration | null {
  try {
    const raw = localStorage.getItem(ORCH_KEY);
    return raw ? (JSON.parse(raw) as Orchestration) : null;
  } catch {
    return null;
  }
}

function saveOrch(o: Orchestration | null) {
  try {
    if (o) localStorage.setItem(ORCH_KEY, JSON.stringify(o));
    else localStorage.removeItem(ORCH_KEY);
  } catch { /* noop */ }
}

export default function Scanner() {
  const qc = useQueryClient();
  const [refreshKey, setRefreshKey] = useState(0);

  const profileQ = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet<UserProfile>('/api/profile'),
  });

  const [prefs, setPrefs] = useState<JobPreferences>(DEFAULT_PREFS);
  const initLoadedRef = useRef(false);

  useEffect(() => {
    if (!profileQ.data || initLoadedRef.current) return;
    const p = profileQ.data.job_preferences;
    if (p) {
      const merged = migrateJobTypes({ ...DEFAULT_PREFS, ...p });
      // Strip disabled scrapers from historically-saved profiles so the
      // scan ledger's denominator (expectedPairs) reflects only the
      // sources we'll actually run. Without this, prefs.sources lingers
      // with glassdoor / zip_recruiter from older sessions and the
      // status bar shows "1/4 sites" forever (no 4th site ever runs).
      // The debounced auto-save below persists the cleaned list back.
      if (Array.isArray(merged.sources)) {
        merged.sources = merged.sources.filter((s) => !DISABLED_SOURCES.has(s));
      }
      setPrefs(merged);
    }
    initLoadedRef.current = true;
  }, [profileQ.data]);

  const saveMut = useMutation({
    mutationFn: (p: JobPreferences) => apiPost('/api/profile', { job_preferences: p }),
    onSuccess: () => {
      // Make term edits immediately visible in ScanStatusBar (which polls
      // the term-status endpoint on a 30 s idle cadence). Without this
      // invalidation, adding/removing a keyword chip would take up to
      // 30 s to show up. Cheap call — endpoint is just a JSON aggregation.
      qc.invalidateQueries({ queryKey: ['scan-term-status'] });
    },
  });

  // Debounced auto-save (300 ms instead of 600 ms — feels closer to instant
  // for chip edits without spamming the profile endpoint on every keystroke
  // inside the search-term editor).
  const saveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!initLoadedRef.current) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => saveMut.mutate(prefs), 300);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs]);

  // Persistent job runners for stage 1 (API) and stage 2 (external sources).
  // `reattach` makes scans started on another device visible here too —
  // both stages are user-singletons so no payload filter is needed.
  const apiRun = useJobRunner('scanner-api', { reattach: { cmd: 'scan' } });
  const extRun = useJobRunner('scanner-ext', { reattach: { cmd: 'scan_jobspy' } });
  const [orch, setOrch] = useState<Orchestration | null>(() => loadOrch());

  // Cross-device orch hydration. When the reattach above adopts a scan job
  // started on another device, the local `orch` blob is missing — without
  // it, RunDrawer hides Stage 2 entirely (showExt = wantExt = false) and
  // the user sees "Stage 1: API portals · pending" with nothing else,
  // even though Stage 2 may also be running. Fix: synthesise orch from
  // the reattached job(s) so RunDrawer renders identically on every
  // device viewing the same scan.
  //
  // Detection: orch with a non-empty payload was set by THIS device's
  // handleRun (the originating click). Orch with an empty payload, or
  // no orch at all, means we reattached — re-hydrate from job state.
  // Keyed on extRun.jobId so we re-fetch once if Stage 2 activates
  // later than Stage 1.
  const hydratedForExtJobIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!apiRun.isActive && !extRun.isActive) return;
    // Skip: orch was written by this device's handleRun (has real payload).
    if (orch && orch.payload) return;
    if (extRun.isActive && extRun.jobId) {
      if (hydratedForExtJobIdRef.current === extRun.jobId) return;
      hydratedForExtJobIdRef.current = extRun.jobId;
      (async () => {
        try {
          const job = await apiGet<{ payload: string }>(`/api/jobs/${extRun.jobId}`);
          let sources: string[] = [];
          try {
            const p = JSON.parse(job.payload || '{}');
            if (Array.isArray(p?.sources)) sources = p.sources;
          } catch { /* payload may be a raw URL or empty */ }
          const next: Orchestration = { wantExt: true, extSources: sources, payload: job.payload || '' };
          setOrch(next);
          saveOrch(next);
        } catch {
          // Network/auth blip — write a degraded orch so Stage 2 still
          // shows (better than hiding a job we know is running). The
          // next mount will retry the fetch.
          const fallback: Orchestration = { wantExt: true, extSources: [], payload: '' };
          setOrch(fallback);
          saveOrch(fallback);
        }
      })();
      return;
    }
    // Only API stage active — write minimal orch describing "no Stage 2".
    // Don't write it if we already have orch describing exactly that, to
    // avoid an unnecessary state churn.
    if (!orch || orch.wantExt !== false) {
      const next: Orchestration = { wantExt: false, extSources: [], payload: '' };
      setOrch(next);
      saveOrch(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRun.isActive, extRun.isActive, extRun.jobId, orch?.payload]);

  // Auto-open drawer if any stage is currently active (on mount or after navigate-in).
  // The localStorage probe catches the same-device case immediately; the
  // `reattach` adoption above runs async and will set `apiRun.isActive` /
  // `extRun.isActive` once the GET /api/jobs response lands, so the
  // useEffect below also opens the drawer for cross-device sessions.
  const [runDrawer, setRunDrawer] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem('nice2job.job.scanner-api') ||
             !!localStorage.getItem('nice2job.job.scanner-ext');
    } catch { return false; }
  });
  useEffect(() => {
    if (apiRun.isRunning || extRun.isRunning) setRunDrawer(true);
  }, [apiRun.isRunning, extRun.isRunning]);

  // Read-only viewer for the most recent scan's log. Independent of the live
  // RunDrawer above — anyRunning could be false and the user still wants to
  // re-read what just finished. State holds the boolean for the drawer; the
  // PastScanLogDrawer component resolves jobId itself on open.
  const [lastLogOpen, setLastLogOpen] = useState(false);

  // When API stage finishes and external sources were wanted, kick off stage 2
  useEffect(() => {
    if (!orch || !orch.wantExt) return;
    if (!apiRun.isActive) return; // no API job — means stage 2 standalone
    if (apiRun.status !== 'done' && apiRun.status !== 'failed') return;
    if (extRun.isActive) return; // stage 2 already running / done
    // Auto-start stage 2
    extRun.startJob('scan_jobspy', orch.payload).catch(() => { /* noop */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRun.status, apiRun.isActive, orch?.wantExt, extRun.isActive]);

  // When everything finishes, clear orchestration blob and bump refresh
  useEffect(() => {
    if (!orch) return;
    const apiDone = !orch.wantExt || !apiRun.isActive || apiRun.status === 'done' || apiRun.status === 'failed';
    const extDone = !orch.wantExt || !extRun.isActive || extRun.status === 'done' || extRun.status === 'failed';
    const allDone =
      (apiRun.status === 'done' || apiRun.status === 'failed' || !apiRun.isActive) &&
      (extRun.status === 'done' || extRun.status === 'failed' || !extRun.isActive) &&
      apiDone && extDone;
    if (allDone) {
      // Small pause so final log events flush
      const t = window.setTimeout(() => setRefreshKey((k) => k + 1), 800);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRun.status, extRun.status]);

  // Stats
  const portalsQ = useQuery({
    queryKey: ['portals'],
    queryFn: () => apiGet<Portal[]>('/api/portals'),
  });

  const portalsCount = portalsQ.data?.length ?? 0;

  const setP = <K extends keyof JobPreferences>(k: K, v: JobPreferences[K]) =>
    setPrefs((prev) => ({ ...prev, [k]: v }));

  const toggleSource = (k: string) => {
    // Disabled sources are not togglable. UI doesn't dispatch onClick on
    // them, but defending here too in case some other code path hits this.
    if (DISABLED_SOURCES.has(k)) return;
    const s = new Set(prefs.sources);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    setP('sources', [...s]);
  };

  const toggleAll = () => {
    // "All" only ever flips the ENABLED sources. Even if a historical
    // saved profile still has glassdoor/zip in prefs.sources, the All
    // chip ignores them — neither selecting them on "all on" nor
    // surfacing them as "currently selected" for the all-state check.
    const allKeys = SOURCE_CHOICES.filter((c) => !c.disabled).map((c) => c.key);
    const allSelected = allKeys.every((k) => prefs.sources.includes(k));
    setP('sources', allSelected ? [] : allKeys);
  };

  // Title-filter readiness — must be loaded AND ready before any scan
  // dispatch. The TitleFilterIndicator above renders a yellow blocking
  // banner when `!ready`; we mirror that state into `canRun` so both
  // scan buttons disable in lockstep. Server enforces the same gate
  // with HTTP 412, but blocking on the client is faster and friendlier.
  const titleFilterQ = useQuery({
    queryKey: ['title-filter'],
    queryFn: () => apiGet<TitleFilterState>('/api/title-filter'),
    staleTime: 30_000,
  });
  // titleFilterReady is consumed via the new ScanActionBar's own readiness
  // probe; we still read it locally so the hooks order stays stable.
  void titleFilterQ.data?.ready;

  const anyRunning = apiRun.isRunning || extRun.isRunning;

  // "Run scan" — manual one-shot scrape using whatever the user has
  // configured in the panel below (sources, search term, hours, etc.).
  // Direct-dispatches a single scan / scan_jobspy job pair via
  // useJobRunner so the user sees live progress in the RunDrawer and
  // gets immediate per-(site, term) results. No auto-scan state is
  // touched. Use this for "I just want results matching THIS query
  // right now" — e.g. trying a different keyword without committing
  // to a 30-day full bootstrap.
  const handleRun = async () => {
    // Phase C: route through /api/scan/run instead of dispatching child
    // jobs directly. The server (runOneUserTick) reads saved prefs via
    // buildAutoScanPayload, honors the pair_status ledger (so repeated
    // clicks skip terms already done in the current bootstrap), and
    // queues scan / scan_jobspy. useJobRunner.reattach picks them up
    // within ~2 s, so RunDrawer continues to show live log.
    try { await saveMut.mutateAsync(prefs); }
    catch { /* keep going — server reads from disk */ }
    setRunDrawer(true);
    try {
      await apiPost('/api/scan/run', { mode: 'daily' });
    } catch (e) {
      alert(`Scan request failed: ${(e as Error).message}`);
    }
  };

  // Table filter q is decoupled from the scan "Search term". The scan term
  // drives what jobspy queries (can be loose: "ML engineer" → "Language
  // Engineer" etc), so reusing it as a strict display filter silently hides
  // returned rows. Use `tableQ` below if users want to narrow the view.
  const [tableQ, setTableQ] = useState('');
  // Default-aligned with Matches Pending: rows the title filter / Phase 4
  // deterministic gate judged irrelevant get hidden here too. Toggle below
  // re-includes them for one-off auditing ("did the gate kill anything I
  // wanted to see?"). Persisted in localStorage so an audit session
  // survives a refresh.
  const [showDropped, setShowDropped] = useState<boolean>(() => {
    try { return localStorage.getItem('nice2job.scanner.showDropped') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('nice2job.scanner.showDropped', showDropped ? '1' : '0'); }
    catch { /* private mode etc */ }
  }, [showDropped]);
  const filters: ScanHistoryFilters = useMemo(
    () => ({
      sources: Array.isArray(prefs.sources) ? prefs.sources : [],
      postedWithinHours: prefs.hours_old ?? null,
      q: tableQ,
      withSalary: !!prefs.only_with_salary,
      country: prefs.country_indeed || undefined,
      // Lightweight post-filter: hide rows whose title contains NONE of
      // the user's scan search-term tokens. Same predicate the scraper
      // uses for pipeline.md, applied at view time so old off-topic rows
      // (Sports Media "Lecturer", Alumni Intern, etc) stop polluting
      // this table. Empty string → server passes everything through.
      keywords: prefs.search_term || undefined,
      includeDropped: showDropped,
    }),
    [prefs.sources, prefs.hours_old, tableQ, prefs.only_with_salary, prefs.country_indeed, prefs.search_term, showDropped]
  );

  // We need the dropped-count to label the toggle. Issue a tiny
  // include_dropped=1 query for total + droppedTotal — cached for 30s
  // so toggling doesn't beat up the server. Skip when the main query
  // is already requesting include_dropped (its response carries the
  // count and we can pull from there).
  const dropCountQ = useQuery({
    queryKey: ['scan-history-drop-count', prefs.sources, prefs.hours_old, prefs.country_indeed, prefs.search_term, prefs.only_with_salary, tableQ],
    queryFn: () => apiGet<{ total: number; droppedTotal?: number }>('/api/scan-history', {
      limit: 1,
      offset: 0,
      sources: Array.isArray(prefs.sources) && prefs.sources.length
        ? prefs.sources.map((s) => s === 'api' ? 'greenhouse-api,ashby-api,lever-api,workable-api' : s).join(',')
        : undefined,
      posted_within_hours: prefs.hours_old ?? undefined,
      q: tableQ.trim() || undefined,
      with_salary: prefs.only_with_salary ? '1' : undefined,
      country: prefs.country_indeed || undefined,
      keywords: prefs.search_term || undefined,
      include_dropped: '1',
    }),
    staleTime: 30_000,
  });
  const droppedTotal = dropCountQ.data?.droppedTotal ?? 0;

  // Two-click armed pattern for Clear history — matches per-row delete UX.
  // First click: button turns red + shows "Sure?" (3s auto-revert).
  // Second click while armed: fires the actual DELETE.
  const [clearArmed, setClearArmed] = useState(false);
  useEffect(() => {
    if (!clearArmed) return;
    const t = setTimeout(() => setClearArmed(false), 3000);
    return () => clearTimeout(t);
  }, [clearArmed]);

  const clearHistory = async () => {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setClearArmed(false);
    try {
      const res = await fetch('/api/scan-history?confirm=true', {
        method: 'DELETE',
        headers: { 'X-Confirm-Destructive': 'yes' },
      });
      const json = await res.json();
      if (json.ok) {
        qc.invalidateQueries({ queryKey: ['scan-history'] });
        qc.invalidateQueries({ queryKey: ['matches-pending'] });
        qc.invalidateQueries({ queryKey: ['matches-pending-count'] });
        setRefreshKey((k) => k + 1);
      } else {
        alert('Clear failed: ' + (json.error || 'unknown'));
      }
    } catch (e) {
      alert('Clear failed: ' + (e as Error).message);
    }
  };

  // Hide drawer but keep jobs running (state persists in localStorage).
  const hideDrawer = () => {
    setRunDrawer(false);
    qc.invalidateQueries({ queryKey: ['scan-history'] });
    setRefreshKey((k) => k + 1);
  };

  // Fully dismiss: clear localStorage + orchestration. Called only when all done.
  const dismissDrawer = () => {
    setRunDrawer(false);
    apiRun.dismiss();
    extRun.dismiss();
    saveOrch(null);
    setOrch(null);
    qc.invalidateQueries({ queryKey: ['scan-history'] });
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="Sources · always on"
        title="Where we look for you."
        sub={`${portalsCount} sources configured. Scans discover new roles; they land in the table below.`}
        actions={
          <>
            <button
              className="btn"
              onClick={clearHistory}
              title={clearArmed ? 'Click again to permanently wipe scan-history.tsv' : 'Wipe data/scan-history.tsv — does NOT touch reports / tracker / tailored CVs'}
              style={{
                color: clearArmed ? '#fff' : '#b43a3a',
                background: clearArmed ? '#b43a3a' : 'transparent',
                border: clearArmed ? '1px solid #b43a3a' : undefined,
                transition: 'all 120ms',
              }}
            >
              {clearArmed ? 'Click again to confirm' : 'Clear history'}
            </button>
            {anyRunning && !runDrawer && (
              <button className="btn" onClick={() => setRunDrawer(true)}>Scan running…</button>
            )}
            <button
              className="btn"
              onClick={() => setLastLogOpen(true)}
              title="Re-open the log from the most recent scan (last 50 jobs are kept on disk)."
            >
              View last scan log
            </button>
          </>
        }
      />

      {/* Scan action bar + per-term status list. */}
      <ScanActionBar />
      <ScanStatusBar />

      {/* Subtle one-line indicator for the CV-derived title filter.
          Renders nothing when no row exists (no CV uploaded yet). */}
      <TitleFilterIndicator />

      {/* Merged Search & preferences panel */}
      <div className="card" style={{ padding: 22, marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <div className="tab" style={{ marginBottom: 6 }}>
              Search term
              {/* The search term is only used by the scraper sources (LinkedIn,
                  Indeed, Glassdoor, ZipRecruiter). API portals fetch every job
                  exposed by Greenhouse / Ashby / Lever and filter locally
                  using the keywords in portals.yml `title_filter`. Without
                  that nuance, users typing "postdoc" with only API portals
                  enabled get unrelated roles back and assume the field is
                  broken. */}
              {!prefs.sources.some((s) => EXT_SOURCES.has(s)) && (
                <span
                  style={{ marginLeft: 8, color: 'var(--ink3)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}
                  title="Search term is sent to scraper sources (LinkedIn, Indeed, Glassdoor, ZipRecruiter). API portals filter by portals.yml title_filter instead — enable a scraper source to use this field."
                >
                  · ignored for API portals
                </span>
              )}
            </div>
            {/* Multi-keyword chip editor. Stores as semicolon-separated
                string in prefs.search_term for backward compat with
                buildAutoScanPayload + jobspy-backend.mjs's Cartesian
                expansion (one scrape per keyword × source). User adds a
                keyword via Enter, comma, or semicolon; the auto-scan
                will run one search per chip per day.
                Previously this was a single <input> that overflowed for
                users with 4+ keywords. */}
            <SearchTermChipEditor
              value={prefs.search_term}
              onChange={(v) => setP('search_term', v)}
            />
            {/* Cost preview — N keywords × M scraper sources = N×M scrapes
                per day. Helps the user understand why adding lots of
                keywords slows scans / risks LinkedIn rate-limit. */}
            <SearchTermHint
              termCount={
                prefs.search_term
                  .split(';')
                  .map((s) => s.trim())
                  .filter(Boolean).length
              }
              scraperSourceCount={prefs.sources.filter((s) => EXT_SOURCES.has(s)).length}
            />
          </div>
          <div>
            <div className="tab" style={{ marginBottom: 6 }}>Location</div>
            <input
              value={prefs.location}
              onChange={(e) => setP('location', e.target.value)}
              placeholder="London · Remote · EU"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <span className="tab" style={{ width: 72 }}>Source</span>
          <Chip
            variant={
              SOURCE_CHOICES.filter((c) => !c.disabled).every((c) => prefs.sources.includes(c.key))
                ? 'primary' : 'default'
            }
            onClick={toggleAll}
          >
            All
          </Chip>
          {SOURCE_CHOICES.map((c) => {
            const on = prefs.sources.includes(c.key);
            // Disabled sources: grey, no onClick (so the cursor is plain
            // text), tooltip shows why. We don't render with `on` styling
            // even if a stale saved profile still has them in
            // prefs.sources — the chip should never look "active".
            if (c.disabled) {
              return (
                <Chip
                  key={c.key}
                  variant="default"
                  title={c.disabledReason}
                  style={{ opacity: 0.4, cursor: 'not-allowed' }}
                >
                  {c.label}
                </Chip>
              );
            }
            return (
              <Chip key={c.key} variant={on ? 'primary' : 'default'} onClick={() => toggleSource(c.key)}>
                {on ? '✓ ' : ''}{c.label}
              </Chip>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <span className="tab" style={{ width: 72 }}>Posted</span>
          {([[null, 'All'], [24, '24h'], [72, '3d'], [168, '7d'], [720, '30d']] as [number | null, string][]).map(([v, l]) => (
            <Chip key={l} variant={prefs.hours_old === v ? 'primary' : 'default'} onClick={() => setP('hours_old', v)}>
              {l}
            </Chip>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <span className="tab" style={{ width: 72 }}>Type</span>
          {/* "Any" is treated as the un-selected state: empty array. Click it
              to clear all type filters. Other chips toggle membership. */}
          <Chip
            key="any"
            variant={prefs.job_types.length === 0 ? 'primary' : 'default'}
            onClick={() => setP('job_types', [])}
          >
            Any
          </Chip>
          {(['fulltime', 'contract', 'parttime', 'internship'] as const).map((v) => {
            const labelMap: Record<typeof v, string> = {
              fulltime: 'Full-time',
              contract: 'Contract',
              parttime: 'Part-time',
              internship: 'Internship',
            };
            const isOn = prefs.job_types.includes(v);
            return (
              <Chip
                key={v}
                variant={isOn ? 'primary' : 'default'}
                onClick={() => {
                  const next = isOn
                    ? prefs.job_types.filter((t) => t !== v)
                    : [...prefs.job_types, v];
                  setP('job_types', next);
                }}
              >
                {labelMap[v]}
              </Chip>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={prefs.is_remote} onChange={(e) => setP('is_remote', e.target.checked)} />
            Remote only
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={prefs.only_with_salary} onChange={(e) => setP('only_with_salary', e.target.checked)} />
            Only with salary
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <div>
            <div className="tab" style={{ marginBottom: 6 }}>
              Min comp · <span className="num" style={{ fontSize: 14, color: 'var(--ink)' }}>${prefs.min_comp}k</span>
            </div>
            <input
              type="range" min={0} max={300} step={10}
              value={prefs.min_comp}
              onChange={(e) => setP('min_comp', +e.target.value)}
              style={{ width: '100%', accentColor: 'var(--pri)' }}
            />
          </div>
          <div>
            <div className="tab" style={{ marginBottom: 6 }}>
              Scan depth
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Two named depth modes. The underlying limit_per_source value
                  is what the backend reads (and auto-picks pacing from); the
                  chips are just an opinionated UI for "quick refresh" vs
                  "thorough crawl". Legacy stored values (50, 80, 200, etc.)
                  get bucketed into whichever mode they're closer to. */}
              {[
                { key: 'fast', label: 'Fast',  value: 100, blurb: '~1 min/pair · 100 jobs/pair max · 10s between pages' },
                { key: 'deep', label: 'Deep',  value: 500, blurb: '~10 min/pair · 500 jobs/pair max · 30s between pages · safer for rate limits' },
              ].map((m) => {
                const isOn = m.value === 100
                  ? prefs.limit_per_source <= 100
                  : prefs.limit_per_source > 100;
                return (
                  <Chip
                    key={m.key}
                    variant={isOn ? 'primary' : 'default'}
                    onClick={() => setP('limit_per_source', m.value)}
                  >
                    {m.label}
                  </Chip>
                );
              })}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginTop: 5, lineHeight: 1.5 }}>
              {prefs.limit_per_source > 100
                ? <span style={{ color: '#a16832' }}>Deep mode — scans up to 500 jobs/pair with slower pacing. Each pair takes ~10 min; safer for LinkedIn rate limits.</span>
                : <>Fast mode — scans up to 100 jobs/pair quickly. Good for daily incremental refreshes. Every pair still runs to completion.</>}
            </div>
            {/* The "Full scan (first-time)" checkbox lived here. Replaced
                by the dedicated "Full run" button at the top of the page,
                which delegates to auto-scan run-now (Cartesian
                expandPairs + per-pair retry/backoff). */}
          </div>
          <div>
            <div
              className="tab"
              style={{ marginBottom: 6 }}
              title="Restricts results from Indeed, LinkedIn, and Glassdoor to this country. Picking 'Auto' lets each source decide (LinkedIn defaults to global). Stored as job_preferences.country_indeed for back-compat."
            >
              Country (Indeed + LinkedIn)
            </div>
            <select
              value={prefs.country_indeed}
              onChange={(e) => setP('country_indeed', e.target.value)}
              style={inputStyle}
            >
              <option value="">Auto</option>
              {['UK', 'US', 'DE', 'FR', 'CA', 'AU', 'ES', 'IT', 'NL'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginTop: 3, lineHeight: 1.4 }}>
              Applied to Indeed and Glassdoor as country, and to LinkedIn as a
              location filter (e.g. "UK" → "United Kingdom"). ZipRecruiter is
              GDPR-blocked in EU/UK regardless.
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink3)' }}>
          {saveMut.isPending ? 'Saving…' : saveMut.isSuccess ? 'Saved ✓' : 'Auto-saves to your profile.'}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, paddingLeft: 2 }}>
        <input
          value={tableQ}
          onChange={(e) => setTableQ(e.target.value)}
          placeholder="Filter results by company or role…"
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid var(--line)',
            borderRadius: 8,
            background: '#fff',
            fontSize: 13,
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        {/* Audit toggle — by default the Scanner table hides URLs the
            title filter / Phase 4 deterministic gate already judged
            irrelevant, so its total matches Matches Pending. Flip this
            on to see what got auto-filtered. */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--ink2)',
            cursor: droppedTotal > 0 ? 'pointer' : 'not-allowed',
            opacity: droppedTotal > 0 ? 1 : 0.5,
            whiteSpace: 'nowrap',
          }}
          title={
            droppedTotal > 0
              ? 'Toggle the title-filter / deterministic-gate drops back into view. Useful for auditing what the gate caught.'
              : 'Nothing has been auto-filtered for the current source / country / keyword filters.'
          }
        >
          <input
            type="checkbox"
            checked={showDropped}
            disabled={droppedTotal === 0}
            onChange={(e) => setShowDropped(e.target.checked)}
            style={{ accentColor: 'var(--pri)' }}
          />
          Show filtered-out ({droppedTotal.toLocaleString()})
        </label>
        {tableQ && (
          <button className="btn" onClick={() => setTableQ('')} style={{ fontSize: 12 }}>
            Clear
          </button>
        )}
      </div>

      <ScanHistoryTable filters={filters} refreshKey={refreshKey} />

      {runDrawer && (
        <RunDrawer
          apiRun={apiRun}
          extRun={extRun}
          orch={orch}
          prefs={prefs}
          onRetryFailed={async (failedSearches) => {
            // Re-submit a scan_jobspy job that ONLY runs the failed pairs.
            // Server-side, the python loop honours `failed_searches` and
            // skips the Cartesian expansion.
            const extPayload = JSON.stringify({
              term: prefs.search_term || 'AI Engineer',
              location: prefs.location || '',
              isRemote: !!prefs.is_remote,
              hoursOld: prefs.hours_old,
              jobType: prefs.job_types.length === 1 ? prefs.job_types[0] : null,
      jobTypes: prefs.job_types,
              countryIndeed: prefs.country_indeed || null,
              limit: prefs.limit_per_source || 100,
              sources: Array.from(new Set(failedSearches.map((p) => p.site))),
              failed_searches: failedSearches,
            });
            await extRun.startJob('scan_jobspy', extPayload);
          }}
          onRetryAll={async () => {
            const extSources = (orch?.extSources ?? []);
            if (!extSources.length) return;
            const extPayload = JSON.stringify({
              term: prefs.search_term || 'AI Engineer',
              location: prefs.location || '',
              isRemote: !!prefs.is_remote,
              hoursOld: prefs.hours_old,
              jobType: prefs.job_types.length === 1 ? prefs.job_types[0] : null,
      jobTypes: prefs.job_types,
              countryIndeed: prefs.country_indeed || null,
              limit: prefs.limit_per_source || 100,
              sources: extSources,
            });
            await extRun.startJob('scan_jobspy', extPayload);
          }}
          onHide={hideDrawer}
          onDismiss={dismissDrawer}
        />
      )}

      <PastScanLogDrawer
        open={lastLogOpen}
        onClose={() => setLastLogOpen(false)}
        prefs={prefs}
        apiRun={apiRun}
        extRun={extRun}
        onRetryFailed={async (failedSearches) => {
          // Reuse the same retry plumbing as live RunDrawer. Re-queues a
          // scan_jobspy with failed_searches set so only the dropped pairs
          // are re-tried. Closes the past view since the live run now
          // owns the drawer surface.
          const extPayload = JSON.stringify({
            term: prefs.search_term || 'AI Engineer',
            location: prefs.location || '',
            isRemote: !!prefs.is_remote,
            hoursOld: prefs.hours_old,
            jobType: prefs.job_types.length === 1 ? prefs.job_types[0] : null,
            jobTypes: prefs.job_types,
            countryIndeed: prefs.country_indeed || null,
            limit: prefs.limit_per_source || 100,
            sources: failedSearches.map((p) => p.site).filter((s, i, arr) => arr.indexOf(s) === i),
            failed_searches: failedSearches,
          });
          setLastLogOpen(false);
          await extRun.startJob('scan_jobspy', extPayload);
          setRunDrawer(true);
        }}
        onRetryAll={async () => {
          setLastLogOpen(false);
          await handleRun();
        }}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PastScanLogDrawer — read-only viewer for the most recent scan.
// Reuses RunDrawer's friendly UI (scan-complete card, filtered-titles list,
// per-pair summary) instead of showing the raw developer-style log. We do
// this by fetching the previous job's log + payload, then synthesising an
// extRun-shaped object with the past log + status and rendering RunDrawer.
// The server's /api/jobs/:id/stream replays an on-disk log for done jobs
// and closes immediately, so this works for completed runs with zero
// backend changes.
// ────────────────────────────────────────────────────────────────────────────
interface PastJobMeta {
  id:       string;
  cmd:      string;
  payload:  string;
  status:   'pending' | 'running' | 'done' | 'failed';
  created:  number;
  updated:  number;
  code:     number | null;
}

function PastScanLogDrawer({
  open, onClose, prefs, apiRun, extRun, onRetryFailed, onRetryAll,
}: {
  open: boolean;
  onClose: () => void;
  prefs: JobPreferences;
  apiRun: ReturnType<typeof useJobRunner>;
  extRun: ReturnType<typeof useJobRunner>;
  onRetryFailed: (failed: { site: string; term: string }[]) => Promise<void> | void;
  onRetryAll: () => Promise<void> | void;
}) {
  const [job, setJob] = useState<PastJobMeta | null>(null);
  const [log, setLog] = useState('');
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'empty' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!open) {
      setJob(null);
      setLog('');
      setLoadState('idle');
      setErrMsg('');
      return;
    }
    let esRef: EventSource | null = null;
    let cancelled = false;
    setLoadState('loading');
    (async () => {
      try {
        // Filter server-side by cmd + status. A plain unfiltered /api/jobs
        // is capped at limit=50 (recency-sorted by `updated`), so a large
        // batch of evaluate jobs — e.g. an "Evaluate all" of 1000+ URLs —
        // fills the entire top-50 window and pushes older scan jobs out of
        // it, making this drawer report "No previous scan found" even though
        // a scan log exists on disk. Asking the server to pre-filter to scan
        // jobs keeps the last scan visible regardless of evaluate volume.
        const data = await apiGet<{ jobs: PastJobMeta[] }>(
          '/api/jobs?cmd=scan_jobspy,scan&status=done,failed&limit=10'
        );
        if (cancelled) return;
        const last = (data.jobs || []).find((j) =>
          (j.cmd === 'scan_jobspy' || j.cmd === 'scan') &&
          (j.status === 'done' || j.status === 'failed')
        );
        if (!last) {
          setLoadState('empty');
          return;
        }
        setJob(last);
        // Open SSE — server replays the on-disk log for done jobs then closes.
        const es = new EventSource(`/api/jobs/${last.id}/stream`);
        esRef = es;
        let acc = '';
        es.onmessage = (msg) => {
          try {
            const obj = JSON.parse(msg.data) as { type?: string; text?: string };
            if ((obj.type === 'stdout' || obj.type === 'stderr') && obj.text) {
              acc += obj.text;
              setLog(acc);
            }
          } catch { /* skip malformed lines */ }
        };
        es.onerror = () => { es.close(); setLoadState((s) => (s === 'loading' ? 'ready' : s)); };
        // Done jobs close the SSE immediately after replay; flip to "ready"
        // ~2s in regardless so the UI always reveals the parsed summary card.
        const t = window.setTimeout(() => setLoadState((s) => (s === 'loading' ? 'ready' : s)), 2000);
        return () => window.clearTimeout(t);
      } catch (e) {
        if (!cancelled) {
          setErrMsg((e as Error).message || 'Failed to fetch jobs');
          setLoadState('error');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (esRef) { try { esRef.close(); } catch { /* noop */ } }
    };
  }, [open]);

  // Reconstruct an extSources list from the past job's payload so RunDrawer's
  // stage label says e.g. "External sources · LinkedIn + Indeed" instead of
  // showing nothing. Falls back to empty array on parse failure.
  const pastOrch = useMemo<Orchestration | null>(() => {
    if (!job) return null;
    try {
      const p = JSON.parse(job.payload) as { sources?: string[] };
      const sources = Array.isArray(p.sources) ? p.sources.filter((s) => s !== 'api') : [];
      return { wantExt: true, extSources: sources, payload: job.payload };
    } catch {
      return { wantExt: true, extSources: [], payload: job.payload };
    }
  }, [job]);

  // Build extRun-shaped object that feeds RunDrawer the PAST log + status.
  // Spread the live extRun to satisfy the type (cancel/dismiss/startJob never
  // fire from past view because onHide/onDismiss are overridden to onClose).
  const pastExtRun = useMemo(() => ({
    ...extRun,
    jobId:     job?.id ?? null,
    status:    (job?.status ?? 'idle') as ReturnType<typeof useJobRunner>['status'],
    log,
    exitCode:  job?.code ?? null,
    errorLine: null,
    isRunning: false,
    isActive:  !!job,
  }), [extRun, job, log]);

  // Force api stage hidden — past view only ever shows the external scan,
  // not API portals (those rarely fail and their log is uninteresting).
  const pastApiRun = useMemo(() => ({
    ...apiRun,
    isActive: false,
  }), [apiRun]);

  if (!open) return null;

  // Loading / empty / error states — render in a small drawer instead of
  // RunDrawer, since we don't have a job to summarise yet.
  if (loadState !== 'ready' || !job) {
    return (
      <Drawer open onClose={onClose} width={520}>
        <div style={{ padding: '22px 24px', borderBottom: '1px solid var(--line)' }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>Last scan</div>
          <div className="serif" style={{ fontSize: 22, letterSpacing: '-0.02em' }}>
            {loadState === 'loading' ? 'Loading log…' :
             loadState === 'empty'   ? 'No previous scan found.' :
             loadState === 'error'   ? 'Failed to load.' :
             'Loading log…'}
          </div>
        </div>
        <div style={{ padding: '18px 24px', flex: 1, fontSize: 13, color: 'var(--ink2)' }}>
          {loadState === 'loading' && 'Fetching the most recent scan from the job history…'}
          {loadState === 'empty' && (
            <>The server keeps the last 50 jobs on disk. Either no scan has run yet for this account, or older jobs have rolled off the log.</>
          )}
          {loadState === 'error' && (
            <>{errMsg}</>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--line)' }}>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </Drawer>
    );
  }

  return (
    <RunDrawer
      apiRun={pastApiRun}
      extRun={pastExtRun}
      orch={pastOrch}
      prefs={prefs}
      onRetryFailed={onRetryFailed}
      onRetryAll={onRetryAll}
      onHide={onClose}
      onDismiss={onClose}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// RunDrawer — shows live log for the two-stage pipeline
// ────────────────────────────────────────────────────────────────────────────

interface ScanResult {
  completed: { site: string; term: string; count: number }[];
  failed: { site: string; term: string; reason: string }[];
  errors: string[];
  /** New offers that matched a search-term keyword in their title and
   *  were appended to pipeline.md. Older runs (before the title-keyword
   *  filter shipped) may not include this field. */
  pipeline_added?: number;
  /** New offers whose title matched none of the keyword tokens. They
   *  were saved to scan-history.tsv (so we don't re-scrape) but skipped
   *  for pipeline.md so they don't waste evaluate quota. */
  filtered?: number;
  /** Up to 50 sample offers that got filtered out, with reason tag so the
   *  user can audit whether the LLM gate / fit-gate / keyword filter
   *  dropped legit matches. Added in commit fixing the early-stop bug
   *  (raw-count vs match-count) — older runs won't have this field. */
  filtered_samples?: {
    title:   string;
    company: string;
    source:  string;
    url:     string;
    reason:  string;
  }[];
}

interface ScanProgressMsg {
  pair: number;
  pair_total: number;
  site: string;
  term: string;
  page: number;
  page_max: number;
  new_in_pair: number;
  /** Cumulative count across all (site, term) pairs scraped so far in
   *  this run. Older runs (before this field was added) won't have it,
   *  in which case we fall back to new_in_pair. */
  total_new?: number;
  elapsed_s: number;
  phase: 'scrape' | 'sleep_page' | 'sleep_pair';
}

/** Pull the structured end-of-run marker (':::JOBSPY_RESULT:::{...}') out of
 *  the log. The marker name is kept as-is internally for backwards compat
 *  with already-running jobs; the user never sees this string. The backend
 *  emits it once on every run (success or failure) so the SPA can summarise
 *  per-(site, term) outcomes. */
function parseScanResult(log: string): ScanResult | null {
  const idx = log.lastIndexOf(':::JOBSPY_RESULT:::');
  if (idx < 0) return null;
  const after = log.slice(idx + ':::JOBSPY_RESULT:::'.length);
  const eol = after.indexOf('\n');
  const json = eol < 0 ? after : after.slice(0, eol);
  try { return JSON.parse(json) as ScanResult; } catch { return null; }
}

/** Pull the LATEST '[PROGRESS]:::{...}' marker out of the log so we can
 *  drive a real progress bar. Both incremental and full mode emit at
 *  least one marker per (site, term); full mode also emits per page. */
function parseLatestProgress(log: string): ScanProgressMsg | null {
  const idx = log.lastIndexOf('[PROGRESS]:::');
  if (idx < 0) return null;
  const after = log.slice(idx + '[PROGRESS]:::'.length);
  const eol = after.indexOf('\n');
  const json = eol < 0 ? after : after.slice(0, eol);
  try { return JSON.parse(json) as ScanProgressMsg; } catch { return null; }
}

function RunDrawer({
  apiRun, extRun, orch, prefs, onRetryFailed, onRetryAll, onHide, onDismiss,
}: {
  apiRun: ReturnType<typeof useJobRunner>;
  extRun: ReturnType<typeof useJobRunner>;
  orch: Orchestration | null;
  prefs: JobPreferences;
  onRetryFailed: (failed: { site: string; term: string }[]) => Promise<void> | void;
  onRetryAll: () => Promise<void> | void;
  onHide: () => void;
  onDismiss: () => void;
}) {
  const wantExt = !!orch?.wantExt;
  const extSources = orch?.extSources ?? [];
  const drawerNav = useNavigate();

  // Detect external scraper not installed
  const extLog = extRun.log.toLowerCase();
  const scraperMissing =
    extLog.includes('python-jobspy is not installed') ||
    extLog.includes('modulenotfounderror') ||
    extLog.includes('no module named');

  // Parse the structured per-(site,term) outcome from the log so we can
  // show a precise summary + retry-only-failed action.
  const extResult = parseScanResult(extRun.log);
  const extProgress = parseLatestProgress(extRun.log);
  const extTerminal = extRun.status === 'done' || extRun.status === 'failed';
  // Suppress prefs warning — used by the parent component when wiring retry payloads.
  void prefs;

  const showApi = apiRun.isActive;
  const showExt = wantExt;

  const allDone =
    (!showApi || apiRun.status === 'done' || apiRun.status === 'failed') &&
    (!showExt || extRun.status === 'done' || extRun.status === 'failed' || extRun.status === 'idle');

  // Stop-scan visibility: any in-flight stage. Kills the matching server-side
  // child via useJobRunner.cancel() (DELETE /api/jobs/:id). Necessary
  // because manual scans bypass auto_scan_state — AutomationCard's
  // Stop scan only covers auto-scan-tracked jobs, leaving manual ones
  // with no UI off-switch when they hang (e.g. LinkedIn anti-bot).
  const anyRunning = apiRun.isRunning || extRun.isRunning;
  const [stopping, setStopping] = useState(false);
  const onStop = async () => {
    if (stopping) return;
    if (!confirm('Stop the scan currently in progress? Anything already scraped (visible in scan-history) is preserved.')) return;
    setStopping(true);
    try {
      // Route through /api/auto-scan/cancel — that endpoint awaits child
      // exit, ingests partial pairs into pair_status, then marks
      // bootstrap skipped so the next cron tick doesn't auto-fire the
      // same scan again. Plain useJobRunner.cancel() (= DELETE
      // /api/jobs/:id) skips all three steps and was the cause of:
      //   - lost partial progress on cancel
      //   - "I cancelled the scan but it kept starting itself" — worker
      //     recovery + Phase-5 retry re-queued because bootstrap state
      //     was still 'in progress'.
      await apiPost('/api/auto-scan/cancel');
      // Clear local useJobRunner state so the drawer UI reflects the cancel
      // immediately even before its next 2 s poll lands a 404 → idle.
      apiRun.dismiss();
      extRun.dismiss();
    } catch (e) {
      alert(`Stop failed: ${(e as Error).message}`);
    } finally {
      setStopping(false);
    }
  };

  const onBackdrop = allDone ? onDismiss : onHide;

  return (
    <Drawer open onClose={onBackdrop} width={560}>
      <div style={{
        padding: '22px 24px',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>Scanning</div>
          <div className="serif" style={{ fontSize: 26, letterSpacing: '-0.02em' }}>Looking for new roles.</div>
        </div>
        {anyRunning && (
          <button
            className="btn"
            onClick={onStop}
            disabled={stopping}
            title="Cancel the running scan. Anything already scraped is kept; cancelled pairs retry on the next 12 h tick."
            style={{
              color: '#b43a3a',
              border: '1px solid var(--line2)',
              background: 'transparent',
              whiteSpace: 'nowrap',
            }}
          >
            {stopping ? 'Stopping…' : 'Stop scan'}
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {showApi && (
          <StageBlock
            number={1}
            label="API portals"
            status={apiRun.status}
            log={apiRun.log}
          />
        )}

        {showExt && (
          <StageBlock
            number={showApi ? 2 : 1}
            label={`External sources · ${extSources.map(pretty).join(' + ')}`}
            status={extRun.status === 'idle' ? 'pending' : extRun.status}
            log={extRun.log}
          >
            {/* Hero progress card driven by [PROGRESS]::: markers.
                Visible while running AND after completion (so the user
                sees "Found N new jobs in M min" before they close the
                drawer). Hidden only when no markers yet (very first
                seconds before Python emits) and not in scraperMissing
                error state (the install-scraper hint is more useful). */}
            {extProgress && !scraperMissing && (
              <ScanProgress
                p={extProgress}
                terminal={
                  extRun.status === 'done'   ? 'done'
                  : extRun.status === 'failed' ? 'failed'
                  : null
                }
              />
            )}

            {scraperMissing && (
              <div style={{ marginTop: 12, padding: '14px 16px', background: '#fcebe8', border: '1px solid #edbfb7', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, color: 'var(--bad)', marginBottom: 6 }}>
                  ⚠ External sources unavailable
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5, marginBottom: 10 }}>
                  The scraper helper for LinkedIn / Indeed / Glassdoor / ZipRecruiter is missing. Run this once, then retry:
                </div>
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6,
                    fontFamily: 'JetBrains Mono, var(--cjk-sans), monospace', fontSize: 11.5,
                  }}
                >
                  <code style={{ flex: 1, overflowX: 'auto' }}>
                    /opt/homebrew/bin/python3.12 -m pip install --break-system-packages --user python-jobspy
                  </code>
                  <button
                    className="btn ghost"
                    style={{ padding: '4px 8px' }}
                    onClick={() => navigator.clipboard.writeText('/opt/homebrew/bin/python3.12 -m pip install --break-system-packages --user python-jobspy')}
                    title="Copy command"
                  >
                    <Copy size={13} />
                  </button>
                </div>
              </div>
            )}

            {/* Per-(site, term) outcome summary + retry card. Always
                shown once the job reaches a terminal status and we
                successfully parsed the JOBSPY_RESULT marker. Hidden
                when scraperMissing fires (the install copy above is
                more useful in that case). */}
            {extTerminal && extResult && !scraperMissing && (extResult.failed.length > 0 || extResult.completed.length > 0) && (
              <ExtScanSummary
                result={extResult}
                running={extRun.isRunning}
                onRetryFailed={onRetryFailed}
                onRetryAll={onRetryAll}
              />
            )}
          </StageBlock>
        )}

        {allDone && (
          <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--pri-soft)', border: '1px solid #d4e2d6', borderRadius: 8, fontSize: 12, color: 'var(--pri)', lineHeight: 1.55 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 8 }}>
              ✓ Scan finished
            </div>
            <div style={{ color: 'var(--ink2)', fontSize: 12.5, marginBottom: 12 }}>
              Next step: head to <strong>Matches → Pending</strong> to triage the new URLs and start evaluating roles against your CV.
            </div>
            <button
              className="btn"
              onClick={() => { onDismiss(); drawerNav('/matches?tab=pending'); }}
              style={{
                background: 'var(--pri)',
                color: '#fff',
                border: '1px solid var(--pri)',
                fontSize: 12.5,
                fontWeight: 600,
                padding: '7px 14px',
              }}
            >
              Go to Matches → Pending →
            </button>
            <div style={{ marginTop: 12, color: 'var(--ink3)', fontSize: 11 }}>
              The count above is what was added to <code>scan-history.tsv</code>.
              Matches further filters by your <em>country</em> and source toggles, so
              fewer rows may appear there.
            </div>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--line)', padding: '14px 24px', display: 'flex', gap: 10 }}>
        {allDone ? (
          <button className="btn" onClick={onDismiss}>Close</button>
        ) : (
          <button className="btn" onClick={onHide}>Hide (keeps running)</button>
        )}
      </div>
    </Drawer>
  );
}

function pretty(k: string): string {
  if (k === 'zip_recruiter') return 'ZipRecruiter';
  return k.charAt(0).toUpperCase() + k.slice(1);
}

function StageBlock({
  number, label, status, log, children,
}: {
  number: number;
  label: string;
  status: 'idle' | 'pending' | 'running' | 'done' | 'failed';
  log: string;
  children?: React.ReactNode;
}) {
  const done = status === 'done' || status === 'failed';
  const active = status === 'pending' || status === 'running';
  const pct = done ? 100 : Math.min(90, log.length / 20);

  // Raw-log details. Open by default while the stage is active so the
  // user has something to look at — especially on the cross-device
  // case where they reattached to a job they didn't start and the rest
  // of the UI shows just "pending". After the user manually toggles,
  // we follow their choice (uncontrolled-style).
  const [logOpen, setLogOpen] = useState(active);
  useEffect(() => {
    // Auto-open when this stage transitions into pending/running.
    if (active) setLogOpen(true);
  }, [active]);

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 22, height: 22, borderRadius: '50%',
            background: done ? (status === 'failed' ? 'var(--bad)' : 'var(--pri)')
                      : active ? 'var(--accent2)' : 'var(--line)',
            color: done ? '#fff' : 'var(--ink)',
            display: 'grid', placeItems: 'center',
            fontSize: 11, fontWeight: 600, fontFamily: 'JetBrains Mono, var(--cjk-sans), monospace',
          }}
        >
          {status === 'failed' ? '×' : done ? '✓' : number}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>Stage {number}: {label}</div>
        <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{status}</div>
      </div>
      <Progress value={pct} />
      {children}
      {/* Raw log: collapsed by default once a stage finishes (most users
          don't need to read 'Searching [linkedin] for: ML Lecturer
          (pair 3/8)' after the fact), but auto-expanded while running
          / pending so the user has SOMETHING to look at — especially
          important on the cross-device case where they reattached to
          a job they didn't start and the hero card would otherwise be
          empty. defaultOpen sets the initial state only; user can still
          toggle freely after that. */}
      <details
        open={logOpen}
        onToggle={(e) => setLogOpen((e.currentTarget as HTMLDetailsElement).open)}
        style={{ marginTop: 12 }}
      >
        <summary
          style={{
            cursor: 'pointer',
            fontSize: 11.5,
            color: 'var(--ink3)',
            userSelect: 'none',
            padding: '4px 0',
          }}
        >
          View details (raw log)
        </summary>
        <pre
          style={{
            marginTop: 8,
            fontFamily: 'JetBrains Mono, var(--cjk-sans), monospace',
            fontSize: 11,
            lineHeight: 1.55,
            color: 'var(--ink2)',
            padding: '10px 12px',
            background: 'var(--line2)',
            borderRadius: 6,
            maxHeight: 280,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {log || (active ? 'Waiting for output…\n' : '')}
        </pre>
      </details>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6,
  fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
};

// ────────────────────────────────────────────────────────────────────────────
// SearchTermChipEditor — multi-keyword input for the Scanner page
// ────────────────────────────────────────────────────────────────────────────
//
// Backed by a single semicolon-separated string (`prefs.search_term`) so the
// existing Cartesian-expansion path in jobspy-backend.mjs continues to work
// without schema changes. Each chip is one search term; auto-scan runs one
// scrape per chip per source per day.
//
// UX:
//   - Wrap chips in a paragraph-style flex box that grows vertically — no
//     more truncation when the user has 6+ keywords.
//   - Add via Enter, comma, or semicolon (matches how users naturally type
//     lists). Empty / duplicate adds are silently no-ops.
//   - Remove with the × on each chip.
//   - Hint line below tells the user how many auto-scan searches/day this
//     implies so the cost is obvious.
//
// Wire shape: parent owns the string; we parse on render, serialise on every
// mutation. No useEffect needed — derived state stays in sync.

function SearchTermHint({
  termCount,
  scraperSourceCount,
}: {
  termCount: number;
  scraperSourceCount: number;
}) {
  if (termCount === 0) {
    return (
      <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
        Tip: add several specific role titles (e.g. "AI Security Engineer", "ML Research Scientist") rather than one broad term. Each chip becomes one search per day.
      </div>
    );
  }
  const totalScrapes = scraperSourceCount === 0 ? 0 : termCount * scraperSourceCount;
  return (
    <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
      {termCount} keyword{termCount === 1 ? '' : 's'}
      {scraperSourceCount === 0
        ? ' — enable a scraper source (LinkedIn / Indeed / Glassdoor) to use them.'
        : ` × ${scraperSourceCount} scraper source${scraperSourceCount === 1 ? '' : 's'} = ${totalScrapes} search${totalScrapes === 1 ? '' : 'es'} per scan run.`}
    </div>
  );
}

function SearchTermChipEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const terms = useMemo(
    () =>
      value
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean),
    [value],
  );
  const [draft, setDraft] = useState('');

  const writeBack = (next: string[]) => onChange(next.join('; '));

  const addDraft = () => {
    const trimmed = draft.trim().replace(/[;,]$/, '').trim();
    if (!trimmed) {
      setDraft('');
      return;
    }
    if (terms.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      setDraft('');
      return;
    }
    writeBack([...terms, trimmed]);
    setDraft('');
  };

  const removeAt = (idx: number) => {
    writeBack(terms.filter((_, i) => i !== idx));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addDraft();
    } else if (e.key === 'Backspace' && draft === '' && terms.length > 0) {
      // Quick remove: empty input + backspace pops the last chip
      removeAt(terms.length - 1);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        padding: '6px 8px',
        border: '1px solid var(--line)',
        borderRadius: 6,
        background: 'var(--bg)',
        minHeight: 38,
        alignItems: 'flex-start',
      }}
    >
      {terms.map((t, i) => (
        <span
          key={`${t}-${i}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 8px',
            background: 'var(--line2)',
            border: '1px solid var(--line)',
            borderRadius: 4,
            fontSize: 12.5,
            lineHeight: 1.3,
            maxWidth: '100%',
          }}
          title={t}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t}
          </span>
          <button
            onClick={() => removeAt(i)}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--ink3)',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
            }}
            title="Remove keyword"
            type="button"
          >
            <X size={11} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={addDraft}
        placeholder={terms.length === 0 ? 'Add a keyword and press Enter…' : 'Add another…'}
        style={{
          flex: 1,
          minWidth: 140,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: 13,
          padding: '3px 4px',
        }}
      />
      {draft.trim() && (
        <button
          className="btn ghost"
          onClick={addDraft}
          type="button"
          style={{ padding: '3px 8px', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 3 }}
          title="Add this keyword"
        >
          <Plus size={11} /> Add
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ScanProgress — friendly hero card for the running scan. Three metrics
// dominate (jobs found · % complete · ETA); one short status line below
// describes the current phase in plain language. Works for both
// incremental and full mode.
// ────────────────────────────────────────────────────────────────────────────

function prettySite(slug: string): string {
  switch (slug) {
    case 'linkedin': return 'LinkedIn';
    case 'indeed': return 'Indeed';
    case 'glassdoor': return 'Glassdoor';
    case 'zip_recruiter':
    case 'ziprecruiter': return 'ZipRecruiter';
    default: return slug.charAt(0).toUpperCase() + slug.slice(1);
  }
}

function ScanProgress({ p, terminal }: { p: ScanProgressMsg; terminal?: 'done' | 'failed' | null }) {
  // Global percent across all (site, term) pairs.
  // Each pair contributes 1/pair_total of total progress.
  // Within a pair:
  //   - full mode  : page/page_max ratio (fine grain)
  //   - incremental: page_max=0 means "no pagination" — treat current
  //                  scrape phase as 50% of pair (we're mid-search but
  //                  don't know finer granularity). After the pair
  //                  completes, the next iteration's emit advances pair.
  const pairShare = p.pair_total > 0 ? 1 / p.pair_total : 0;
  const isPaged   = p.page_max > 0;
  const inPairProgress =
    p.phase === 'sleep_pair' ? 1
    : isPaged                 ? Math.min(1, p.page / p.page_max)
    : 0.5; // incremental: count "in flight" as halfway through this pair
  const finishedPairs = Math.max(0, p.pair - 1);
  const pctRaw = Math.min(100, Math.round(((finishedPairs + inPairProgress) * pairShare) * 100));
  const pct = terminal === 'done' ? 100 : pctRaw;

  // ETA: linear extrapolation from elapsed × (100/pct − 1).
  let etaLabel = '';
  if (!terminal && p.elapsed_s && pct > 3 && pct < 100) {
    const totalEst = p.elapsed_s * (100 / pct);
    const remaining = Math.max(0, totalEst - p.elapsed_s);
    const m = Math.ceil(remaining / 60);
    etaLabel = m < 1 ? '<1 min remaining' : `~${m} min remaining`;
  }

  const totalNew = typeof p.total_new === 'number' ? p.total_new : (p.new_in_pair || 0);
  const elapsedMin = Math.max(1, Math.round(p.elapsed_s / 60));

  // Plain-language status line.
  let statusLine: string;
  if (terminal === 'done') {
    statusLine = `Finished in ${elapsedMin} min`;
  } else if (terminal === 'failed') {
    statusLine = `Stopped early — see View details below or the retry card`;
  } else if (p.phase === 'sleep_page') {
    statusLine = 'Resting briefly to stay under the LinkedIn rate limit…';
  } else if (p.phase === 'sleep_pair') {
    statusLine = 'Finished one search · pausing a moment before the next';
  } else {
    statusLine = `Searching ${prettySite(p.site)} for "${p.term}"`;
  }

  // Color states: running (purple), done (green), failed (red).
  const palette = terminal === 'done'
    ? { bg: 'var(--pri-soft)', border: '#d4e2d6', accent: 'var(--pri)', heading: 'var(--pri)' }
    : terminal === 'failed'
    ? { bg: '#fef2f2', border: '#fca5a5', accent: '#b91c1c', heading: '#7f1d1d' }
    : { bg: 'var(--surface)', border: 'var(--line)', accent: 'var(--pri)', heading: 'var(--ink)' };

  return (
    <div
      style={{
        marginTop: 12, padding: '18px 20px',
        border: `1px solid ${palette.border}`, borderRadius: 12,
        background: palette.bg,
      }}
    >
      <div style={{ fontSize: 11.5, color: 'var(--ink2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {terminal === 'done' ? 'Scan complete' : terminal === 'failed' ? 'Scan stopped' : 'Scanning external job boards'}
      </div>
      {/* Hero number — large serif, dominant. */}
      <div className="serif" style={{ fontSize: 36, fontWeight: 400, letterSpacing: '-0.02em', color: palette.heading, lineHeight: 1.1 }}>
        {totalNew} new {totalNew === 1 ? 'job' : 'jobs'}
      </div>

      {/* Progress bar + percent + ETA. */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 14, marginBottom: 6, gap: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--ink2)' }}>
          <strong style={{ color: 'var(--ink)' }}>{pct}%</strong> complete
        </span>
        {etaLabel && (
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{etaLabel}</span>
        )}
      </div>
      <div style={{ height: 8, background: 'var(--bg)', borderRadius: 4, overflow: 'hidden' }}>
        <div
          style={{
            height: '100%', width: `${pct}%`,
            background: palette.accent,
            transition: 'width 600ms ease',
          }}
        />
      </div>

      {/* Plain-language status. */}
      <div style={{ marginTop: 12, fontSize: 13, color: 'var(--ink2)', lineHeight: 1.5 }}>
        {statusLine}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ExtScanSummary — render the per-(site, term) outcome of a scan_jobspy run
// and offer Retry-only-failed / Retry-all controls.
// ────────────────────────────────────────────────────────────────────────────

function ExtScanSummary({
  result,
  running,
  onRetryFailed,
  onRetryAll,
}: {
  result: ScanResult;
  running: boolean;
  onRetryFailed: (failed: { site: string; term: string }[]) => Promise<void> | void;
  onRetryAll: () => Promise<void> | void;
}) {
  const ok = result.completed.length;
  const okJobs = result.completed.reduce((s, c) => s + (c.count || 0), 0);
  const failed = result.failed.length;
  const allOk = failed === 0;
  const failedPairs = result.failed.map((f) => ({ site: f.site, term: f.term }));
  // Title-keyword filter counts. Optional (older runs before the
  // filter shipped won't have them); when present and > 0 we add a
  // muted secondary line so the user knows why pipeline.md may be
  // smaller than the raw new-jobs total.
  const pipelineAdded = result.pipeline_added;
  const filteredOut = result.filtered;
  const banner = allOk
    ? { bg: 'var(--pri-soft)', border: '#d4e2d6', color: 'var(--pri)', icon: '✓' }
    : { bg: '#fef2f2', border: '#fca5a5', color: '#7f1d1d', icon: '⚠️' };
  return (
    <div
      style={{
        marginTop: 12,
        padding: '14px 16px',
        background: banner.bg,
        border: `1px solid ${banner.border}`,
        borderRadius: 10,
        fontSize: 13,
        color: banner.color,
        lineHeight: 1.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>{banner.icon}</span>
        <strong style={{ fontSize: 14 }}>
          {allOk
            ? `Scan complete · ${ok}/${ok} searches succeeded · ${okJobs} new URLs added to scan history`
            : `Partial result · ${ok} succeeded, ${failed} failed · ${okJobs} new URLs added`}
        </strong>
      </div>
      {/* Honest secondary line. The previous wording ("N added to evaluate
          inbox") made it sound like all N would appear in Matches Pending,
          but Matches re-applies country / source / keyword filters when
          rendering, so the visible count is usually a fraction of N. Spell
          out both numbers and that the visible count will be smaller. */}
      {typeof filteredOut === 'number' && (
        <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: -4, marginBottom: 8, lineHeight: 1.55 }}>
          {pipelineAdded ?? 0} matched your keywords{filteredOut > 0 ? ` · ${filteredOut} off-topic (kept in scan history but never appears in Pending)` : ''}.
          <br />
          <span style={{ color: 'var(--ink3)' }}>
            Matches → Pending applies country / source filters on display, so the visible count there will be smaller.
          </span>
        </div>
      )}

      {/* Sample of filtered-out titles — lets the user audit whether the
          LLM gate / fit-gate / keyword filter dropped any legit matches.
          Only show if we have samples (newer runs) AND at least one drop. */}
      {Array.isArray(result.filtered_samples) && result.filtered_samples.length > 0 && (
        <details style={{ marginBottom: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ink2)' }}>
            Show {result.filtered_samples.length} filtered title{result.filtered_samples.length === 1 ? '' : 's'}
            {(filteredOut ?? 0) > result.filtered_samples.length ? ` (of ${filteredOut} total)` : ''}
          </summary>
          <div style={{ margin: '8px 0 0', maxHeight: 260, overflowY: 'auto', background: '#fff', border: '1px solid #e8e2d4', borderRadius: 6, padding: '6px 0' }}>
            {result.filtered_samples.map((s, i) => (
              <div
                key={i}
                style={{
                  padding: '6px 10px',
                  borderBottom: i < result.filtered_samples!.length - 1 ? '1px solid #f4eee0' : 'none',
                  fontSize: 12,
                  color: 'var(--ink2)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ink)', fontWeight: 500, textDecoration: 'none' }}>
                        {s.title || '(no title)'}
                      </a>
                    ) : (
                      <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{s.title || '(no title)'}</span>
                    )}
                    {s.company ? <span style={{ color: 'var(--ink3)' }}> · {s.company}</span> : null}
                  </div>
                  <code style={{ background: '#faf6ec', padding: '1px 6px', borderRadius: 3, fontSize: 10.5, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>
                    {s.source}
                  </code>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 2 }}>
                  Dropped by: {s.reason}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {failed > 0 && (
        <details style={{ marginBottom: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ink2)' }}>
            Show {failed} failed search{failed === 1 ? '' : 'es'}
          </summary>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 12, color: 'var(--ink2)' }}>
            {result.failed.map((f, i) => (
              <li key={i}>
                <code style={{ background: '#fff', padding: '1px 5px', borderRadius: 3 }}>{f.site}</code>{' '}
                <span>{f.term}</span> — <span style={{ color: '#a16363' }}>{f.reason}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {failed > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn primary"
            disabled={running}
            onClick={() => onRetryFailed(failedPairs)}
          >
            {running ? 'Retrying…' : `Retry ${failed} failed search${failed === 1 ? '' : 'es'}`}
          </button>
          <button
            className="btn"
            disabled={running}
            onClick={() => onRetryAll()}
            title="Re-run every search from scratch (succeeded + failed). Slower."
          >
            Retry all
          </button>
        </div>
      )}
    </div>
  );
}
